import { SignupPage, SignupPageProps } from '@zapier/auth-pages';
import type { InvitationResponse, Invitation } from '@zapier/authentication';
import { getFacebookLeadAdsRedirectUrl } from '@zapier/authentication';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';

import { PageLayout, Title } from '../../components';

export default function SignUp({ invitationData }: SignupPageProps) {
  return (
    <Title title="Sign up">
      <PageLayout>
        <SignupPage invitationData={invitationData} />
      </PageLayout>
    </Title>
  );
}

export const getServerSideProps: GetServerSideProps<SignupPageProps> = async (
  context: GetServerSidePropsContext
) => {
  // Specifically for Facebook Lead Ads, we redirect the user to a partner signup URL.
  if (context.req.url && process.env.NEXT_PUBLIC_ZAPIER_ORIGIN) {
    const url = await getFacebookLeadAdsRedirectUrl(
      context.req.url,
      process.env.NEXT_PUBLIC_ZAPIER_ORIGIN
    );

    if (url) {
      return {
        redirect: {
          permanent: false,
          destination: url,
        },
        props: {},
      };
    }
  }

  const code = context.query.invitation as string;
  if (code) {
    try {
      const invitation = await getInvitationByCode(context, code);

      return {
        props: {
          invitationData: {
            invitation,
            isValid: Boolean(invitation?.status === 'pending'),
            code,
          },
        },
      };
    } catch (err: any) {
      return {
        props: {
          invitationData: {
            error: err.message || 'There was an unexpected error',
            isValid: false,
            code,
          },
        },
      };
    }
  } else {
    // No invitation to fetch, return nothing for invitation
    return {
      props: {},
    };
  }
};

async function getInvitationByCode(
  context: GetServerSidePropsContext,
  code: string
): Promise<Invitation> {
  const baseUrl = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  const headers: HeadersInit = new Headers();
  headers.set('cookie', context.req.headers.cookie || '');

  const response = await fetch(`${baseUrl}/api/v4/invitations/${code}`, {
    headers,
  });
  const result = await response.json();

  if (response.status !== 200) {
    // Look for possible error properties
    throw new Error(result?.error);
  }

  // Success
  const body = result as InvitationResponse;

  return {
    code,
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso,
    },
    ssoUrl: body.sso_url,
    email: body.email,
    id: body.id,
    status: body.status,
    createdBy: body.created_by,
  };
}
